// Phase 3.2 step 3: full strict typing — no @ts-nocheck.
import { MATH_MODULES } from "../curriculum/mathModules.js";
import { READING_MODULES } from "../curriculum/readingModules.js";
// masteryTracker (v1) was retired during the engine consolidation.
// MA-155 (retake-placement-wiring, 2026-07-21): buildPlacementSession's
// and scorePlacement's only live UI caller (useSessionLifecycle.js's
// handleSessionFinish, which seeded skipped-module v2 mastery records
// right after scorePlacement returned) was removed as dead code — the
// legacy PlacementScreen it served was a confirmed dead end (see
// App.jsx's decision log). Both functions stay here as build-time
// sampling utilities several scripts/audit/*.mjs scripts still use.
import { createPlacementValidation } from "./placementValidation.js";
import type { ParentPriors, Session, Item, Domain, ResponseMode } from "./types.ts";
import { ageToBand, bandRank } from "./ageBand.ts";
// MA-775 (RED M2): placementBandForAge / defaultsForAge / applyPriorBoost /
// shiftModule live in the leaf module ./ageStartModules.ts (no placement or
// engine imports), so the progress layer can read the age → start table
// without making placementEngine/placementValidation reachable from the
// ADR-069 mastery pipelines. Re-exported here: every existing importer is
// unchanged.
export { placementBandForAge, defaultsForAge, applyPriorBoost, shiftModule, type AgeDefaults } from "./ageStartModules.ts";
import { defaultsForAge, applyPriorBoost, placementBandForAge, shiftModule } from "./ageStartModules.ts";


interface PlacementItemExtra {
  type?: string;
  visualType?: string | null;
  visualData?: Record<string, unknown>;
  isTransfer?: boolean;
  audioData?: Record<string, unknown> | null;
}

function item(
  id: string,
  domain: Domain,
  moduleId: string,
  skillId: string,
  prompt: string,
  correctAnswer: string | number,
  distractors: Array<string | number>,
  responseMode: ResponseMode = "handwrite_then_confirm",
  extra: PlacementItemExtra = {},
): Item {
  return {
    id,
    domain,
    moduleId,
    skillId,
    type: extra.type || "placement",
    prompt,
    correctAnswer,
    distractors,
    responseMode,
    visualType: extra.visualType || null,
    visualData: extra.visualData || {},
    isTransfer: Boolean(extra.isTransfer),
    isReview: false,
    placementItem: true,
    audioData: extra.audioData || null,
  };
}

function ageNumber(progress: { studentAge?: number | string; age_years?: number | string } | null | undefined): number {
  const age = Number(progress?.studentAge ?? progress?.age_years ?? 6);
  return Number.isFinite(age) ? age : 6;
}

/**
 * MA-81 — the start modules after a parent changes the child's age.
 *
 * `mathModuleId` / `readingModuleId` are the child's start modules: onboarding
 * seeds them from `applyPriorBoost(defaultsForAge(age), priors)` and, once
 * placement is complete (every cloud-linked child), they are the per-strand
 * placement floors of the focal pick, whose floor skill is exempt from the age
 * masks. Leaving them at the OLD age's start after an age change lets the pick
 * serve a skill the tree badges "For younger ages" to the re-banded child.
 *
 * Per strand:
 *   - newStart = the start onboarding seeds for `newAge` (same priors boost);
 *   - the module is missing / unknown → newStart;
 *   - the module equals the OLD age's start (purely age-derived, no evidence
 *     on it):
 *       · moving UP (newStart is later on the ladder) → newStart;
 *       · moving DOWN → newStart, UNLESS the module's own skill is banded at or
 *         below the new band (`skillBandForModule`): then it is already not
 *         above the child, and lowering it would only move the floor further
 *         below the band. (RED round 1 M3: a 7-year-old re-banded to 6 kept
 *         `math.11` = `ms.math.11`, band 6.0-6.5, on band; lowering it to the
 *         age-6 start `math.09`/`reading.06` served the "For younger ages"
 *         `ms.read.06` — the age-6 start itself sits below the tree's cover
 *         line, a separate pre-existing gap.)
 *   - otherwise it carries evidence (e.g. the onboarding diagnostic raise) →
 *     the later of the module and newStart by curriculum order: evidence is
 *     kept, but the floor never stays below the new band's start ("placement
 *     raises a floor, never lowers it", placementFloor.ts).
 * `oldAge` null/non-finite → no old start is known (the evidence branch).
 * `skillBandForModule` maps a legacy module id to its microskill's age band
 * (null when unknown → the module is treated as above the band). Pure;
 * `changed` is true when either strand moved.
 */
export function startModulesAfterAgeChange({
  oldAge,
  newAge,
  mathModuleId,
  readingModuleId,
  parentPriors,
  skillBandForModule,
}: {
  oldAge: number | null | undefined;
  newAge: number;
  mathModuleId?: string | null;
  readingModuleId?: string | null;
  parentPriors?: ParentPriors | null;
  skillBandForModule: (moduleId: string) => string | null | undefined;
}): { mathModuleId: string; readingModuleId: string; changed: boolean } {
  const newStart = applyPriorBoost(defaultsForAge(newAge), parentPriors);
  const oldStart =
    typeof oldAge === "number" && Number.isFinite(oldAge)
      ? applyPriorBoost(defaultsForAge(oldAge), parentPriors)
      : null;
  const newBandRank = bandRank(ageToBand(newAge));
  const pick = (
    modules: CurriculumModule[],
    current: string | null | undefined,
    newStartId: string,
    oldStartId: string | null,
  ): string => {
    const at = (id: string | null | undefined) => (id ? modules.findIndex((m) => m.id === id) : -1);
    if (at(current) < 0) return newStartId;
    const cur = current as string;
    if (cur === oldStartId) {
      if (at(newStartId) >= at(cur)) return newStartId;
      const band = skillBandForModule(cur);
      return band && bandRank(band) <= newBandRank ? cur : newStartId;
    }
    return at(cur) >= at(newStartId) ? cur : newStartId;
  };
  const math = pick(MATH_MODULES as CurriculumModule[], mathModuleId, newStart.mathModuleId, oldStart?.mathModuleId ?? null);
  const reading = pick(READING_MODULES as CurriculumModule[], readingModuleId, newStart.readingModuleId, oldStart?.readingModuleId ?? null);
  return {
    mathModuleId: math,
    readingModuleId: reading,
    changed: math !== mathModuleId || reading !== readingModuleId,
  };
}

interface ProgressLike {
  studentAge?: number | string;
  age_years?: number | string;
  mathModuleId?: string;
  readingModuleId?: string;
  parentPriors?: ParentPriors | null;
  skillStates?: Record<string, unknown>;
  [k: string]: unknown;
}

const MOTOR_WARMUP = [
  item("motor_01", "handwriting", "handwriting.tap", "handwriting.tap_target", "Tap the star.", "⭐", ["⭐", "🌙", "☁️", "❤️"], "tap_choice", { type: "motor_tap" }),
  item("motor_02", "handwriting", "handwriting.digit", "handwriting.digit_7", "Write 7.", 7, [1, 6, 9], "handwrite_then_confirm", { type: "motor_digit", visualType: "numeral_writing", visualData: { numeral: 7, showTrace: false } }),
  item("motor_03", "handwriting", "handwriting.letter", "handwriting.letter_m", "Write m.", "m", ["n", "w", "u"], "handwrite_then_confirm", { type: "motor_letter", visualType: "letter_trace", visualData: { letter: "m", showTrace: false } }),
];

const MATH_PLACEMENT_AGE6 = [
  item("m6_place_01", "math", "math.09", "math.operations.addition_within_10", "Write the answer: 6 + 3 = ?", 9, [8, 10, 7], "handwrite_then_confirm", { type: "add", visualType: "ten_frame_number_line", visualData: { a: 6, b: 3, operation: "add", max: 10 } }),
  item("m6_place_02", "math", "math.10", "math.operations.subtraction_within_10", "Write the answer: 9 − 4 = ?", 5, [4, 6, 13], "handwrite_then_confirm", { type: "subtract", visualType: "ten_frame_number_line", visualData: { start: 9, remove: 4, operation: "subtract", max: 10 } }),
  item("m6_place_03", "math", "math.11", "math.operations.addition_within_20_make_10", "Write the answer: 8 + 5 = ?", 13, [12, 14, 15], "handwrite_then_confirm", { type: "add", visualType: "ten_frame_number_line", visualData: { a: 8, b: 5, operation: "add", max: 20 } }),
  item("m6_place_04", "math", "math.12", "math.operations.missing_addends_within_20", "6 + ___ = 13", 7, [6, 8, 9], "handwrite_then_confirm", { type: "missing_addend", visualType: "number_bond", visualData: { whole: 13, part1: 6, part2: "?" } }),
  item("m6_place_05", "math", "math.11", "math.operations.subtraction_within_20", "Write the answer: 14 − 6 = ?", 8, [7, 9, 10], "handwrite_then_confirm", { type: "subtract", visualType: "number_line", visualData: { start: 14, jumps: 6, direction: "left", max: 20 } }),
  item("m6_place_06", "math", "math.13", "math.place_value.teen_numbers", "Which number is 1 ten and 6 ones?", 16, [61, 15, 17], "tap_choice", { type: "place_value", visualType: "place_value_blocks", visualData: { tens: 1, ones: 6 } }),
  item("m6_place_07", "math", "math.14", "math.operations.two_digit_plus_10", "Write the answer: 23 + 10 = ?", 33, [24, 32, 43], "handwrite_then_confirm", { type: "two_digit_add" }),
  item("m6_place_08", "math", "math.11", "math.word_problems.join_within_20", "Sasha has 9 shells. She finds 5 more. How many shells does she have now?", 14, [13, 15, 4], "handwrite_then_confirm", { type: "word_problem", isTransfer: true }),
];

const READING_PLACEMENT_AGE6 = [
  item("r6_place_01", "reading", "reading.06", "reading.phonics.cvc_decoding", "Read this word. Tap the matching word: cat", "cat", ["cap", "cot", "cut"], "tap_choice", { type: "cvc_decode", visualType: "word_display", visualData: { word: "cat" } }),
  item("r6_place_02", "reading", "reading.07", "reading.phonics.cvc_encoding", "Write the word: map", "map", ["mat", "mop", "nap"], "handwrite_then_confirm", { type: "cvc_encode", visualType: "sound_boxes_writing", visualData: { word: "map", phonemes: ["/m/", "/ă/", "/p/"], boxCount: 3 } }),
  item("r6_place_03", "reading", "reading.09", "reading.phonics.cvcc_decoding", "Read this word. Tap the matching word: best", "best", ["bet", "bent", "nest"], "tap_choice", { type: "blend_word", visualType: "word_display", visualData: { word: "best" } }),
  item("r6_place_04", "reading", "reading.09", "reading.phonics.ccvc_decoding", "Read this word. Tap the matching word: frog", "frog", ["fog", "flag", "from"], "tap_choice", { type: "blend_word", visualType: "word_display", visualData: { word: "frog" } }),
  item("r6_place_05", "reading", "reading.11", "reading.phonics.digraph_sh", "Read this word. Tap the matching word: ship", "ship", ["sip", "shop", "chip"], "tap_choice", { type: "digraph", visualType: "word_display", visualData: { word: "ship" } }),
  item("r6_place_06", "reading", "reading.08", "reading.word_reading.high_frequency_the", "Write the word: the", "the", ["then", "they", "that"], "handwrite_then_confirm", { type: "hfw", visualType: "heart_word_mapping", visualData: { word: "the", type: "heart" } }), // MA-1191: per-word breakdown dropped (D-1102-3)
  item("r6_place_07", "reading", "reading.10", "reading.sentences.controlled_completion", "The frog can ___.", "jump", ["jam", "run", "sit"], "handwrite_then_confirm", { type: "sentence_complete", visualType: "sentence_with_blank", visualData: { template: "The frog can ___." } }),
  item("r6_place_08", "reading", "reading.10", "reading.language.sentence_meaning", "Choose the word that makes sense: The sun is ___.", "hot", ["hat", "hit", "hut"], "tap_choice", { type: "sentence_meaning", visualType: "sentence_with_blank", visualData: { template: "The sun is ___." }, isTransfer: true }),
];

// Age 4 placement: count objects, recognize numerals. NO arithmetic.
const MATH_PLACEMENT_AGE4 = [
  item("m4_place_01", "math", "math.00a", "math.number_sense.count_to_5", "How many?", 3, [2, 3, 4], "tap_choice", { type: "count", visualType: "countable_objects", visualData: { count: 3, objectType: "apples", positions: [[80,75],[125,75],[170,75]] } }),
  item("m4_place_02", "math", "math.00a", "math.number_sense.count_to_5", "How many?", 5, [3, 4, 5], "tap_choice", { type: "count", visualType: "countable_objects", visualData: { count: 5, objectType: "stars", positions: [[80,75],[125,75],[170,75],[215,75],[260,75]] } }),
  item("m4_place_03", "math", "math.00c", "math.number_sense.recognize_5", "Tap the 3.", 3, [1, 2, 4], "tap_choice", { type: "recognize_numeral", visualType: "none", visualData: {} }),
  item("m4_place_04", "math", "math.00d", "math.number_sense.recognize_10", "Tap the 7.", 7, [4, 5, 9], "tap_choice", { type: "recognize_numeral", visualType: "none", visualData: {} }),
  item("m4_place_05", "math", "math.00b", "math.number_sense.count_to_10", "How many?", 8, [6, 7, 8], "tap_choice", { type: "count", visualType: "countable_objects", visualData: { count: 8, objectType: "dogs", positions: [[80,75],[125,75],[170,75],[215,75],[80,130],[125,130],[170,130],[215,130]] } }),
];

const MATH_PLACEMENT_AGE5 = [
  item("m5_place_01", "math", "math.05", "math.composition.compose_5", "3 + ? = 5", 2, [1, 3, 4], "handwrite_then_confirm", { type: "compose", visualType: "five_frame", visualData: { filled: 3, target: 5 } }),
  item("m5_place_02", "math", "math.07", "math.operations.addition_within_5", "2 + 3 = ?", 5, [4, 3, 6], "handwrite_then_confirm", { type: "add", visualType: "part_part_whole", visualData: { a: 2, b: 3, part1: 2, part2: 3 } }),
  item("m5_place_03", "math", "math.08", "math.operations.subtraction_within_5", "5 − 2 = ?", 3, [2, 4, 7], "handwrite_then_confirm", { type: "subtract", visualType: "takeaway_objects", visualData: { start: 5, remove: 2 } }),
  ...MATH_PLACEMENT_AGE6.slice(0, 5),
];

const READING_PLACEMENT_AGE5 = [
  item("r5_place_01", "reading", "reading.02", "reading.alphabet.letter_sounds", "Which letter spells /m/?", "m", ["n", "s", "t"], "tap_choice", { type: "letter_sound" }),
  item("r5_place_02", "reading", "reading.04", "reading.phonemic_awareness.oral_blending", "Blend: /m/ /ă/ /t/. What word?", "mat", ["map", "sat", "tap"], "tap_choice", { type: "blend" }),
  ...READING_PLACEMENT_AGE6.slice(0, 6),
];

const MATH_PLACEMENT_AGE7 = [
  item("m7_place_01", "math", "math.11", "math.operations.addition_within_20_make_10", "Write the answer: 9 + 6 = ?", 15, [14, 16, 17], "handwrite_then_confirm", { type: "add", visualType: "number_line", visualData: { a: 9, b: 6, start: 9, jumps: 6, direction: "right", max: 20 } }),
  item("m7_place_02", "math", "math.11", "math.operations.subtraction_within_20", "Write the answer: 16 - 7 = ?", 9, [8, 10, 11], "handwrite_then_confirm", { type: "subtract", visualType: "number_line", visualData: { start: 16, remove: 7, jumps: 7, direction: "left", max: 20 } }),
  item("m7_place_03", "math", "math.12", "math.operations.missing_addends_within_20", "8 + ___ = 17", 9, [8, 10, 11], "handwrite_then_confirm", { type: "missing_addend", visualType: "number_bond", visualData: { whole: 17, part1: 8, part2: "?" } }),
  item("m7_place_04", "math", "math.13", "math.place_value.two_digit_numbers", "Which number is 3 tens and 4 ones?", 34, [43, 33, 35], "tap_choice", { type: "place_value", visualType: "place_value_blocks", visualData: { tens: 3, ones: 4 } }),
  item("m7_place_05", "math", "math.14", "math.operations.two_digit_plus_10", "Write the answer: 23 + 10 = ?", 33, [24, 32, 43], "handwrite_then_confirm", { type: "two_digit_add" }),
  item("m7_place_06", "math", "math.15", "math.word_problems.join_within_20", "Sasha has 9 shells. She finds 5 more. How many shells does she have now?", 14, [13, 15, 4], "handwrite_then_confirm", { type: "word_problem_join", isTransfer: true }),
];

const READING_PLACEMENT_AGE7 = [
  item("r7_place_01", "reading", "reading.09", "reading.phonics.cvcc_decoding", "Read this word. Tap the matching word: best", "best", ["bet", "bent", "nest"], "tap_choice", { type: "blend_word", visualType: "word_display", visualData: { word: "best" } }),
  item("r7_place_02", "reading", "reading.11", "reading.phonics.digraph_sh", "Read this word. Tap the matching word: ship", "ship", ["sip", "shop", "chip"], "tap_choice", { type: "digraph", visualType: "word_display", visualData: { word: "ship" } }),
  item("r7_place_03", "reading", "reading.11", "reading.phonics.digraph_ck", "Write the word: duck", "duck", ["dock", "luck", "back"], "handwrite_then_confirm", { type: "digraph", visualType: "word_display", visualData: { word: "duck" } }),
  item("r7_place_04", "reading", "reading.12", "reading.phonics.silent_e", "Read the word: make", "make", ["mat", "mack", "map"], "tap_choice", { type: "silent_e", visualType: "word_display", visualData: { word: "make", markSilentE: true } }),
  item("r7_place_05", "reading", "reading.10", "reading.sentences.controlled_completion", "The frog can ___.", "jump", ["jam", "run", "sit"], "handwrite_then_confirm", { type: "sentence_complete", visualType: "sentence_with_blank", visualData: { template: "The frog can ___." } }),
  item("r7_place_06", "reading", "reading.13", "reading.language.controlled_passage", "Sam needs water. Mom hands him a ___.", "cup", ["cap", "cot", "cub"], "handwrite_then_confirm", { type: "passage_complete", visualType: "passage_with_blank", visualData: { template: "Sam needs water. Mom hands him a ___." }, isTransfer: true }),
];

// MA-P0-6: placement is a short adaptive check, not a full session.
// Cap scored domains to 6 items each; motor warmup is not scored and is never capped.
// Applied AFTER the age-band routing so each band's item selection is preserved.
const PLACEMENT_DOMAIN_CAP = 6;

function placementItemsForAge(age: number | string): { mathItems: Item[]; readingItems: Item[] } {
  const band = placementBandForAge(age);
  let mathItems: Item[];
  let readingItems: Item[];
  if (band === "4.0") {
    mathItems = MATH_PLACEMENT_AGE4;
    readingItems = READING_PLACEMENT_AGE5.slice(0, 2); // letter sound + oral blend only
  } else if (band === "4.5") {
    mathItems = [...MATH_PLACEMENT_AGE4, ...MATH_PLACEMENT_AGE5.slice(0, 2)];
    readingItems = READING_PLACEMENT_AGE5.slice(0, 3);
  } else if (band === "5.0") {
    mathItems = MATH_PLACEMENT_AGE5.slice(0, 6);
    readingItems = READING_PLACEMENT_AGE5.slice(0, 6);
  } else if (band === "5.5") {
    mathItems = MATH_PLACEMENT_AGE5;
    readingItems = READING_PLACEMENT_AGE5;
  } else if (band === "6.0") {
    mathItems = [MATH_PLACEMENT_AGE5[0], ...MATH_PLACEMENT_AGE6.filter((it) => it.moduleId !== "math.14")];
    readingItems = READING_PLACEMENT_AGE6.slice(0, 7);
  } else if (band === "6.5") {
    mathItems = MATH_PLACEMENT_AGE6;
    readingItems = READING_PLACEMENT_AGE6;
  } else {
    mathItems = MATH_PLACEMENT_AGE7;
    readingItems = READING_PLACEMENT_AGE7;
  }
  return {
    mathItems: mathItems.slice(0, PLACEMENT_DOMAIN_CAP),
    readingItems: readingItems.slice(0, PLACEMENT_DOMAIN_CAP),
  };
}

export function buildPlacementSession(progress: Record<string, unknown> = {}): Session {
  const age = ageNumber(progress);
  const { mathItems, readingItems } = placementItemsForAge(age);
  // motor_01 ("Tap the star") is only meaningful for age 5 — skip it for older children.
  // motor_02/03 (write 7, write m) are still useful at age 6 to check pencil control.
  // For age 4 — skip motor entirely (handwriting too hard at this age).
  const motorItems = age >= 7 ? [] : age >= 6 ? MOTOR_WARMUP.slice(1) : age >= 5 ? MOTOR_WARMUP : [];
  const items = [...motorItems, ...mathItems, ...readingItems].map((it) => ({ ...it, id: `${it.id}_${Date.now().toString(36)}` }));
  return {
    id: `placement_${Date.now().toString(36)}`,
    domain: "placement",
    moduleId: "placement",
    items,
    plannedTotal: items.length,
    reviewSkillIds: [],
    startedAt: new Date().toISOString(),
    completedAt: null,
    results: [],
    adaptiveEvents: [],
    placementVersion: "v3",
    placementAgeBand: placementBandForAge(age),
  };
}

interface DomainStats {
  total: number;
  correct: number;
  transferTotal: number;
  transferCorrect: number;
  efficient: number;
  byModule: Map<string, { correct: number; total: number }>;
}

interface ResultRow {
  moduleId?: string;
  correct?: boolean;
  isTransfer?: boolean;
  latencyMs?: number;
  responseMode?: string;
}

function countDomain(results: ResultRow[], domain: string): DomainStats {
  const filtered = results.filter((r) => r.moduleId?.startsWith(`${domain}.`));
  const correct = filtered.filter((r) => r.correct).length;
  const transfer = filtered.filter((r) => r.isTransfer);
  const transferCorrect = transfer.filter((r) => r.correct).length;
  const efficient = filtered.filter((r) => r.correct && (r.latencyMs || 0) <= (r.responseMode?.includes("handwrite") ? 15000 : 8000)).length;
  // Per-module roll-up: highest moduleId with strong correctness becomes the
  // placement ceiling. Without this signal, a 6.5yo who aces every item
  // (including reading.11 digraphs and math.14 two-digit add) was capped at
  // age-default + 1 rung — way below where Kumon-level kids actually are.
  const byModule = new Map<string, { correct: number; total: number }>();
  for (const r of filtered) {
    if (!r.moduleId) continue;
    const m = byModule.get(r.moduleId) || { correct: 0, total: 0 };
    m.total += 1;
    if (r.correct) m.correct += 1;
    byModule.set(r.moduleId, m);
  }
  return {
    total: filtered.length, correct,
    transferTotal: transfer.length, transferCorrect, efficient,
    byModule,
  };
}

// Find the highest-indexed module the child demonstrably mastered.
// "Mastered" requires:
//   - all items at that module correct
//   - AND at least 2 items at that module
// The 2+ rule guards against single-item modules (math.13 place-value,
// reading.11 digraph) being claimed by a single lucky tap. Real
// mastery-via-ceiling needs multi-item evidence; single-item correct
// gets credit through the accuracy-shift path instead.
function highestMasteredModule(modules: Array<{ id: string }>, byModule: Map<string, { correct: number; total: number }>): string | null {
  let bestIdx = -1;
  let bestId = null;
  for (let i = 0; i < modules.length; i++) {
    const data = byModule.get(modules[i].id);
    if (!data || data.total < 2) continue;
    if (data.correct === data.total) {
      bestIdx = i;
      bestId = modules[i].id;
    }
  }
  return bestId;
}

interface CurriculumModule { id: string }


function bandForModule(domain: string, moduleId: string): string {
  const modules = (domain === "math" ? MATH_MODULES : READING_MODULES) as CurriculumModule[];
  const idx = modules.findIndex((m) => m.id === moduleId);
  if (idx < 0) return domain === "math" ? "M?" : "R?";
  return `${domain === "math" ? "M" : "R"}${Math.max(1, Math.ceil((idx + 1) / 3))}`;
}

interface PlacementDecisionInput {
  domain: "math" | "reading";
  stats: DomainStats;
  targetModuleId: string;
  ageBand: string;
}

interface PlacementDecisionOutput {
  band: string;
  moduleId: string;
  confidence: string;
  reason: string;
  accuracy: number;
  transferAccuracy: number;
}

function placementDecision({ domain, stats, targetModuleId, ageBand }: PlacementDecisionInput): PlacementDecisionOutput {
  const modules = domain === "math" ? MATH_MODULES : READING_MODULES;
  const accuracy = stats.total ? stats.correct / stats.total : 0;
  const transferAccuracy = stats.transferTotal ? stats.transferCorrect / stats.transferTotal : accuracy;
  let shift = 0;
  let confidence = "medium";
  let reason = `${domain === "math" ? "Math" : "Reading"} placement matched the ${ageBand}-year starting band.`;

  if (accuracy < 0.4) {
    shift = -2;
    reason = `${domain === "math" ? "Math" : "Reading"} check was below the age-band target, so practice starts with more foundation work.`;
  } else if (accuracy < 0.65) {
    shift = -1;
    reason = `${domain === "math" ? "Math" : "Reading"} check showed partial readiness, so practice starts one step before the age target.`;
  } else if (accuracy >= 0.9 && transferAccuracy >= 0.5) {
    shift = 1;
    confidence = "high";
    reason = `${domain === "math" ? "Math" : "Reading"} check was strong, including transfer work, so practice starts one step ahead.`;
  } else if (accuracy >= 0.75) {
    confidence = "high";
  }

  // Accuracy-based candidate (the legacy path).
  const accuracyCandidateId = shiftModule(modules, targetModuleId, shift);

  // Ceiling-based candidate: the highest module the kid actually nailed.
  // For a Kumon-track 6.5yo who aces every placement item including
  // reading.11 digraphs, this lets us place at reading.11 instead of
  // capping at age-default + 1 rung. We MAX the two so we never demote a
  // kid who scored well overall but missed the highest module's transfer
  // item — the accuracy path still protects them.
  const ceilingId = stats.byModule ? highestMasteredModule(modules, stats.byModule) : null;
  const ceilingIdx = ceilingId ? modules.findIndex((m) => m.id === ceilingId) : -1;
  const accuracyIdx = modules.findIndex((m) => m.id === accuracyCandidateId);
  const moduleId: string = ceilingIdx > accuracyIdx && ceilingId ? ceilingId : accuracyCandidateId;
  if (moduleId === ceilingId && ceilingIdx > accuracyIdx) {
    confidence = "high";
    reason = `${domain === "math" ? "Math" : "Reading"} check was strong all the way through — practice starts at the highest module the placement covered.`;
  }

  return {
    band: bandForModule(domain, moduleId),
    moduleId,
    confidence,
    reason,
    accuracy,
    transferAccuracy,
  };
}

/** List of modules that the placement results say the kid can skip
 * (the modules ranked below the placed-into one). Historically the caller
 * seeded v2 mastery records for these (see the file header — that caller
 * was removed in MA-155 as dead code); kept exported for tests. */
function skippedModulesBefore(modules: CurriculumModule[], targetModuleId: string): string[] {
  const targetIdx = modules.findIndex((m) => m.id === targetModuleId);
  if (targetIdx <= 0) return [];
  return modules.slice(0, targetIdx).map((m) => m.id);
}

export function scorePlacement(finalSession: Session, previousProgress: ProgressLike = {}): {
  placementStatus: "complete";
  mathModuleId: string;
  readingModuleId: string;
  skippedMathModuleIds: string[];
  skippedReadingModuleIds: string[];
  placementValidation: unknown;
  placementResults: Record<string, unknown>;
} {
  const age = ageNumber(previousProgress);
  // Apply parent-prior boost to the placement target so an ahead-of-grade
  // kid is assessed against the right baseline. Otherwise scorePlacement
  // would treat a Kumon 6.5yo as if reading.09 were their target, and a
  // strong-but-imperfect pass on advanced items might leave them placed
  // BELOW where the priors say they should be.
  const ageDefaults = applyPriorBoost(defaultsForAge(age), previousProgress.parentPriors);
  const ageBand = placementBandForAge(age);
  const math = countDomain((finalSession.results || []) as ResultRow[], "math");
  const reading = countDomain((finalSession.results || []) as ResultRow[], "reading");
  const mathResult = placementDecision({ domain: "math", stats: math, targetModuleId: ageDefaults.mathModuleId, ageBand });
  const readingResult = placementDecision({ domain: "reading", stats: reading, targetModuleId: ageDefaults.readingModuleId, ageBand });

  return {
    placementStatus: "complete",
    mathModuleId: mathResult.moduleId,
    readingModuleId: readingResult.moduleId,
    skippedMathModuleIds: skippedModulesBefore(MATH_MODULES, mathResult.moduleId),
    skippedReadingModuleIds: skippedModulesBefore(READING_MODULES, readingResult.moduleId),
    placementValidation: createPlacementValidation(mathResult.moduleId, readingResult.moduleId),
    placementResults: {
      mathBand: mathResult.band,
      readingBand: readingResult.band,
      handwritingBand: "ready_with_confirmation",
      mathConfidence: mathResult.confidence,
      readingConfidence: readingResult.confidence,
      mathRaw: math,
      readingRaw: reading,
      reasons: [mathResult.reason, readingResult.reason],
      completedAt: new Date().toISOString(),
      ageBand,
      version: "v3",
    },
  };
}
